import { useSuspendingLiveQuery, useLiveQueryImmediate } from "@/db/hooks"
import React from "react"
import { parseDbDate } from "@totodo/operations"
import {
  db,
  ObjectType,
  ObjectStatus,
  StartAtType,
  StartAtSubType,
} from "@/db/schema"
import type { MemberInfo } from "@/features/navigation/MemberAvatars"
import { useDayStore } from "@/background/stores/dayStore"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A sidebar menu item (Area, Project, or List) with progress data */
export interface MenuItem {
  id: string
  type: ObjectType
  title: string
  status: ObjectStatus
  sortIndex: number
  dueAt?: string
  realmId?: string
  /** Tags assigned directly to this container */
  tags: string[]
  /** Completed child task count (for project pie chart) */
  completedCount: number
  /** Total child task count */
  totalCount: number
  /** Unfinished child task count (for list bar logic) */
  unfinishedCount: number
  /** True when this project/list is deferred (Someday or future-dated) */
  isLater?: boolean
  /** Custom icon string — prefix-routed: "pika:category/name" | "emoji:☕" */
  icon?: string
}

export interface Counters {
  inbox: number
  today: number
  /** Number of today tasks with a deadline due today or overdue */
  todayDeadline: number
}

export interface StaticData {
  menuItems: MenuItem[]
  /** All items including later (deferred) projects — for pickers / autocomplete */
  allMenuItems: MenuItem[]
  counters: Counters
  /** Map of realmId → member emails (excludes current user) */
  memberMap: Record<string, MemberInfo[]>
  /** Map of areaId → count of later (Someday / future-dated) projects hidden from sidebar */
  laterProjectCounts: Map<string, number>
  /** Count of free-standing (not under any area) later projects/lists — drives "{x} later projects" sidebar row */
  freeStandingLaterCount: number
}

// ---------------------------------------------------------------------------
// Query — the single optimized fetch for all sidebar data
// ---------------------------------------------------------------------------

/**
 * Fetch all data the sidebar needs in a single Dexie transaction-like batch:
 *
 * 1. Menu items — compound index `[parentPath+type+isDeleted]` for Areas,
 *    Projects, Lists at "root". Joined with `privateIndex` via `bulkGet`
 *    for sidebar sort order. O(n) where n = menu items (typically 10-30).
 *
 * 2. Progress data — single compound index query `[parentPath+status+isDeleted]`
 *    to get ALL child tasks for ALL projects/lists in ONE query. Aggregated
 *    per parentPath in JS. Replaces v1's N+1 `useGetPercentageCompleted` calls.
 *
 * 3. Inbox count — compound index `[parentPath+status+isDeleted]` →
 *    filter by type=Task → count. No full row loading.
 *
 * 4. Today count — `startAtIndex` type index → filter startAt ≤ today →
 *    `bulkGet` from totodo → count unfinished tasks. Replaces v1's
 *    expensive full `useLiveDataAll("/today")` that loaded ALL today
 *    tasks just for a badge number.
 *
 * 5. Member data — batch query ALL realm members for shared items in
 *    ONE query. Excludes current user. Replaces v1's per-item Avatars hook.
 *
 * Total: ~7 IDB operations, all index-assisted. <15ms on mobile.
 */
async function fetchStaticData(): Promise<StaticData> {
  // ------------------------------------------------------------------
  // 1. Menu items: Areas, Projects, Lists at root — unfinished only
  // ------------------------------------------------------------------
  // Per-key getAll() (keys in ascending order, as anyOf() would walk them);
  // anyOf() + .filter() iterates a cursor with a main-thread round trip per row.
  const rawItems = (
    await Promise.all(
      [
        ["root", ObjectType.Area, 0],
        ["root", ObjectType.Project, 0],
        ["root", ObjectType.List, 0],
      ].map((key) =>
        db.totodo.where("[parentPath+type+isDeleted]").equals(key).toArray(),
      ),
    )
  )
    .flat()
    .filter((item) => item.status === ObjectStatus.Unfinished)

  // Batch fetch sort indexes — O(1) per item via bulkGet
  // privateIndex uses "#" + id as primary key (same convention as startAtIndex)
  const privateIds = rawItems.map((i) => `#${i.id}`)
  const sortEntries = await db.privateIndex.bulkGet(privateIds)

  // Build sort map: privateIndex.id → privateIndex.index
  const sortMap = new Map<string, number>()
  privateIds.forEach((pid, i) => {
    if (sortEntries[i]) sortMap.set(pid, sortEntries[i]!.index)
  })

  // ------------------------------------------------------------------
  // 2. Progress data: batch all child tasks for all projects/lists
  //    ONE query replaces v1's N+1 useGetPercentageCompleted per item
  // ------------------------------------------------------------------
  const projectsAndLists = rawItems.filter(
    (i) => i.type === ObjectType.Project || i.type === ObjectType.List,
  )

  // Build parentPath tuples: [parentPath, status, isDeleted] for compound index
  // We need both completed and unfinished tasks to compute percentages
  const progressTuples = projectsAndLists.flatMap((item) => [
    [`root/${item.id}`, ObjectStatus.Unfinished, 0],
    [`root/${item.id}`, ObjectStatus.Completed, 0],
  ])

  // One equals().toArray() per tuple — each is a single getAll(). anyOf() +
  // .filter() made Dexie walk a cursor with a main-thread round trip per child
  // (5,300 rows on a large account), holding a readonly transaction open for
  // seconds at startup and on every re-run, which blocks totodo writes.
  const childTasks = (
    await Promise.all(
      progressTuples.map((tuple) =>
        db.totodo.where("[parentPath+status+isDeleted]").equals(tuple).toArray(),
      ),
    )
  )
    .flat()
    .filter((t) => t.type === ObjectType.Task)

  // Aggregate per parent: { parentPath → { completed, total, unfinished } }
  const progressMap = new Map<
    string,
    { completed: number; total: number; unfinished: number }
  >()
  for (const task of childTasks) {
    const parentPath = task.parentPath
    let entry = progressMap.get(parentPath)
    if (!entry) {
      entry = { completed: 0, total: 0, unfinished: 0 }
      progressMap.set(parentPath, entry)
    }
    entry.total++
    if (task.status === ObjectStatus.Completed) entry.completed++
    else entry.unfinished++
  }

  // Enrich with sort index and progress, then sort
  const menuItems: MenuItem[] = rawItems
    .map((item) => {
      const progress = progressMap.get(`root/${item.id}`) ?? {
        completed: 0,
        total: 0,
        unfinished: 0,
      }
      return {
        id: item.id!,
        type: item.type,
        title: item.title,
        status: item.status,
        sortIndex: sortMap.get(`#${item.id}`) ?? item.index,
        dueAt: item.dueAt,
        realmId: item.realmId,
        tags: item.tags ?? [],
        completedCount: progress.completed,
        totalCount: progress.total,
        unfinishedCount: progress.unfinished,
        icon: item.icon,
      }
    })
    .sort((a, b) => a.sortIndex - b.sortIndex)

  // ------------------------------------------------------------------
  // 1b. Later projects: mark deferred projects and sort to bottom of area group.
  //     A project/list is "later" if startAtIndex has Someday type or
  //     Default type with startAt > end-of-today.
  // ------------------------------------------------------------------
  const projectListIds = menuItems
    .filter((i) => i.type === ObjectType.Project || i.type === ObjectType.List)
    .map((i) => `#${i.id}`)
  const startAtEntries =
    projectListIds.length > 0
      ? await db.startAtIndex.bulkGet(projectListIds)
      : []

  // Compute end-of-today for "later" threshold
  const todayEndForLater = new Date()
  todayEndForLater.setHours(23, 59, 59, 999)
  const todayEndTs = todayEndForLater.getTime()

  const laterIds = new Set<string>()
  projectListIds.forEach((pid, i) => {
    const entry = startAtEntries[i]
    if (!entry) return
    if (entry.type === StartAtType.Someday) {
      laterIds.add(pid.slice(1)) // strip leading #
    } else if (
      entry.type === StartAtType.Default &&
      (parseDbDate(entry.startAt) ?? 0) > todayEndTs
    ) {
      laterIds.add(pid.slice(1))
    }
  })

  // Collect recurring template IDs (for hiding from sidebar)
  const recurringTemplateIds = new Set<string>()
  for (const item of rawItems) {
    if (
      item.isRecurringTemplate === 1 &&
      (item.type === ObjectType.Project || item.type === ObjectType.List)
    ) {
      recurringTemplateIds.add(item.id!)
    }
  }

  // Mark later items, count per area, and filter them OUT of sidebar.
  // Free-standing later containers (not under any area) get their own count
  // for the "{x} later projects" sidebar row.
  const laterProjectCounts = new Map<string, number>()
  let freeStandingLaterCount = 0
  let currentAreaForCount: string | null = null
  for (const item of menuItems) {
    if (item.type === ObjectType.Area) {
      currentAreaForCount = item.id
    } else if (
      laterIds.has(item.id) || recurringTemplateIds.has(item.id)
    ) {
      // Someday/future-dated projects AND recurring templates are "later"
      item.isLater = true
      if (currentAreaForCount) {
        laterProjectCounts.set(
          currentAreaForCount,
          (laterProjectCounts.get(currentAreaForCount) ?? 0) + 1,
        )
      } else {
        freeStandingLaterCount++
      }
    }
  }

  // Filter out later items entirely — they are hidden from sidebar.
  // Areas are always kept. Active projects/lists are kept.
  const visibleMenuItems = menuItems.filter(
    (item) => item.type === ObjectType.Area || !item.isLater,
  )

  // ------------------------------------------------------------------
  // 2. Inbox count: unfinished tasks in inbox — compound index count
  // ------------------------------------------------------------------
  const inboxCount = await db.totodo
    .where("[parentPath+status+isDeleted]")
    .equals(["root/#inbox", ObjectStatus.Unfinished, 0])
    .filter((t) => t.type === ObjectType.Task)
    .count()

  // ------------------------------------------------------------------
  // 3. Today count: startAtIndex → totodo join (lightweight vs v1)
  //
  // v1 runs a FULL useLiveDataAll("/today") just for this number —
  // loading all task objects with all fields. We do a targeted count:
  //   - Query startAtIndex.type index for Default entries
  //   - Filter startAt ≤ end-of-today, subType = Default (not evening)
  //   - bulkGet only IDs from totodo for status check
  // ------------------------------------------------------------------
  const tomorrow = new Date()
  tomorrow.setHours(0, 0, 0, 0)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowTs = tomorrow.getTime()

  const todayEntries = await db.startAtIndex
    .where("type")
    .equals(StartAtType.Default)
    .filter(
      (sai) =>
        (parseDbDate(sai.startAt) ?? Infinity) < tomorrowTs &&
        sai.subType === StartAtSubType.Default,
    )
    .toArray()

  // Join: strip leading "#" from startAtIndex.id to get totodo.id
  const taskIds = todayEntries
    .map((s) => s.id!)
    .filter(Boolean)
    .map((id) => id.slice(1))
  const tasks = await db.totodo.bulkGet(taskIds)
  const todayTasks = tasks.filter(
    (t) =>
      t != null &&
      t.status === ObjectStatus.Unfinished &&
      t.isDeleted === 0 &&
      (t.type === ObjectType.Task ||
        t.type === ObjectType.Project ||
        t.type === ObjectType.List),
  )
  const todayCount = todayTasks.length

  // Deadline count: today tasks with dueAt ≤ today (due today or overdue)
  const todayDeadline = todayTasks.filter(
    (t) =>
      t!.dueAt != null &&
      t!.dueAt.length > 0 &&
      (parseDbDate(t!.dueAt) ?? Infinity) < tomorrowTs,
  ).length

  // ------------------------------------------------------------------
  // 5. Member data: batch ALL shared item members in ONE query
  //    Replaces v1's per-item Avatars hooks (N+1 problem)
  // ------------------------------------------------------------------
  const realmIds = [
    ...new Set(rawItems.map((i) => i.realmId).filter(Boolean) as string[]),
  ]
  const memberMap: Record<string, MemberInfo[]> = {}

  if (realmIds.length > 0) {
    try {
      // Dexie Cloud's built-in members table (not in our schema, added by addon)
      const membersTable = db.table("members")
      const currentUser = db.cloud.currentUser.value
      const currentEmail = currentUser?.email ?? ""

      const allMembers = await membersTable
        .where("realmId")
        .anyOf(realmIds)
        .toArray()

      // Group by realmId, exclude current user
      for (const m of allMembers as Array<{
        realmId: string
        email?: string
        owner?: string
        accepted?: boolean
      }>) {
        const rid = m.realmId
        if (!memberMap[rid]) memberMap[rid] = []
        const email = m.email ?? m.owner ?? ""
        if (!email || email === currentEmail) continue
        if (memberMap[rid].some((x) => x.email === email)) continue
        memberMap[rid].push({
          email,
          pending: m.accepted === false,
        })
      }
    } catch {
      // Members table may not exist before cloud sync — silently skip
    }
  }

  return {
    menuItems: visibleMenuItems,
    allMenuItems: menuItems,
    counters: { inbox: inboxCount, today: todayCount, todayDeadline },
    memberMap,
    laterProjectCounts,
    freeStandingLaterCount,
  }
}

// ---------------------------------------------------------------------------
// Hook — Layer 1 persistent query (always active while sidebar is mounted)
// ---------------------------------------------------------------------------

/**
 * Layer 1 persistent hook — sidebar menu data + badge counters.
 *
 * Uses `useSuspendingLiveQuery` with cache key `["static-data"]`.
 * The observable cache ensures a SINGLE Dexie subscription regardless
 * of how many components consume this hook. Auto-tracks table dependencies
 * (totodo, privateIndex, startAtIndex) and re-fires on relevant changes.
 *
 * Suspends until first data load — wrap in `<Suspense>` boundary.
 *
 * @returns `[data, setOptimistic]` — call `setOptimistic` for instant
 *          sidebar reorder during drag-and-drop (Phase 3+).
 */
export function useStaticData(): [
  StaticData,
  React.Dispatch<React.SetStateAction<StaticData | null>>,
] {
  // Subscribe to day changes so later-project classification re-computes at midnight
  const today = useDayStore((s) => s.today)
  return useSuspendingLiveQuery(fetchStaticData, ["static-data", today])
}

// ---------------------------------------------------------------------------
// Sidebar localStorage cache
// ---------------------------------------------------------------------------

const SIDEBAR_CACHE_KEY = "totodo:sidebar"

interface CachedSidebarData {
  menuItems: MenuItem[]
  counters: Counters
  memberMap: Record<string, MemberInfo[]>
  /** Serialised laterProjectCounts (Map → plain object for JSON) */
  laterProjectCounts?: Record<string, number>
  freeStandingLaterCount?: number
  cachedAt: number
}

/** Read cached sidebar data from localStorage */
function readSidebarCache(): StaticData | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_CACHE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as CachedSidebarData
    return {
      menuItems: data.menuItems,
      allMenuItems: data.menuItems,
      counters: data.counters,
      memberMap: data.memberMap,
      laterProjectCounts: new Map(
        Object.entries(data.laterProjectCounts ?? {}),
      ),
      freeStandingLaterCount: data.freeStandingLaterCount ?? 0,
    }
  } catch {
    return null
  }
}

/** Write sidebar data to localStorage */
function writeSidebarCache(data: StaticData): void {
  try {
    const entry: CachedSidebarData = {
      menuItems: data.menuItems,
      counters: data.counters,
      memberMap: data.memberMap,
      laterProjectCounts: Object.fromEntries(data.laterProjectCounts),
      freeStandingLaterCount: data.freeStandingLaterCount,
      cachedAt: Date.now(),
    }
    localStorage.setItem(SIDEBAR_CACHE_KEY, JSON.stringify(entry))
  } catch {
    // localStorage full — silently ignore
  }
}

const EMPTY_STATIC_DATA: StaticData = {
  menuItems: [],
  allMenuItems: [],
  counters: { inbox: 0, today: 0, todayDeadline: 0 },
  memberMap: {},
  laterProjectCounts: new Map(),
  freeStandingLaterCount: 0,
}

// ---------------------------------------------------------------------------
// useStaticDataImmediate — Non-suspending variant with cache
// ---------------------------------------------------------------------------

/**
 * Layer 1 sidebar data — **non-suspending**.
 *
 * Returns cached sidebar data from localStorage instantly (isLoading=true),
 * then swaps to real data when the Dexie query resolves (isLoading=false).
 *
 * On every real data load, writes to localStorage for next visit.
 * Falls back to empty static data if no cache exists.
 */
export function useStaticDataImmediate(): [
  StaticData,
  React.Dispatch<React.SetStateAction<StaticData | null>>,
  boolean,
] {
  const placeholder = React.useMemo(
    () => readSidebarCache() ?? EMPTY_STATIC_DATA,
    [],
  )

  // Subscribe to day changes so later-project classification re-computes at midnight
  const today = useDayStore((s) => s.today)

  const [data, setOptimistic, isLoading] = useLiveQueryImmediate(
    fetchStaticData,
    ["static-data", today],
    placeholder,
  )

  // Cache real data to localStorage whenever it changes
  const isLoadingRef = React.useRef(isLoading)
  isLoadingRef.current = isLoading

  React.useEffect(() => {
    if (isLoadingRef.current) return
    writeSidebarCache(data)
  }, [data])

  return [data, setOptimistic, isLoading]
}
